import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Paper,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useCompany } from "../../contexts/CompanyContext";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import syntheticStockMoveService from "../../services/syntheticStockMoveService";
import dayjs from "dayjs";
import { usePersistedPagination } from "../../hooks/usePersistedPagination";

const AccountingInboundsTable = () => {
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();

  const currentDate = new Date();
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [paginationModel, setPaginationModel] = usePersistedPagination({
    defaultPageSize: 25,
  });

  const months = [
    { value: 1, label: "Enero" },
    { value: 2, label: "Febrero" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Mayo" },
    { value: 6, label: "Junio" },
    { value: 7, label: "Julio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" },
    { value: 11, label: "Noviembre" },
    { value: 12, label: "Diciembre" },
  ];

  const years = Array.from({ length: 10 }, (_, i) => currentDate.getFullYear() - i);

  const fetchData = async () => {
    if (!selectedCompany) return;

    setLoading(true);
    try {
      const result = await syntheticStockMoveService.getEligiblePurchaseOrders(
        selectedCompany.id,
        month,
        year
      );
      setData(result);
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedCompany, month, year]);

  const handleCreateInbounds = async () => {
    if (selectedRows.length === 0) {
      openSnack("Seleccione al menos una línea de orden de compra", "warning");
      return;
    }

    try {
      setLoading(true);
      await syntheticStockMoveService.createSyntheticInbounds({
        POItemIds: selectedRows,
        WarehouseId: 1,
        CompanyId: selectedCompany.id,
      });
      openSnack("Entradas contables creadas exitosamente", "success");
      setSelectedRows([]);
      await fetchData();
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "poCode",
      headerName: "Código OC",
      flex: 0.12,
    },
    {
      field: "providerName",
      headerName: "Proveedor",
      flex: 0.18,
      valueGetter: (value, row) => {
        return `${row.providerCode} - ${row.providerName}`;
      },
    },
    {
      field: "invoiceDate",
      headerName: "Fecha de Factura",
      flex: 0.12,
      valueFormatter: (value) => {
        if (!value) return "-";
        return dayjs(value).format("DD/MM/YYYY");
      },
    },
    {
      field: "invoiceNo",
      headerName: "No. Factura",
      flex: 0.1,
    },
    {
      field: "raCode",
      headerName: "Contrarrecibo",
      flex: 0.1,
    },
    {
      field: "workOrderId",
      headerName: "Orden de trabajo",
      flex: 0.1,
      valueFormatter: (value) => value || "-",
    },
    {
      field: "productCode",
      headerName: "Producto",
      flex: 0.14,
      valueGetter: (value, row) => row.productCode || "",
    },
    {
      field: "quantity",
      headerName: "Cantidad",
      flex: 0.08,
      valueGetter: (value, row) => `${row.quantity} ${row.unit || ""}`.trim(),
    },
    {
      field: "lineTotal",
      headerName: "Total línea",
      flex: 0.1,
      valueFormatter: (value) => {
        return new Intl.NumberFormat("es-MX", {
          style: "currency",
          currency: "MXN",
        }).format(value || 0);
      },
    },
  ];

  if (!selectedCompany) {
    return (
      <Box p={4}>
        <Typography>Seleccione una compañía primero</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Mes</InputLabel>
              <Select
                value={month}
                label="Mes"
                onChange={(e) => setMonth(e.target.value)}
              >
                {months.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Año</InputLabel>
              <Select
                value={year}
                label="Año"
                onChange={(e) => setYear(e.target.value)}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Chip
              label={`${data.length} líneas elegibles`}
              color="primary"
              variant="outlined"
            />
          </Stack>

          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateInbounds}
            disabled={loading || selectedRows.length === 0}
          >
            Crear Entradas ({selectedRows.length})
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={2} sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={data}
          columns={columns}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => {
            setSelectedRows(newSelection);
          }}
          rowSelectionModel={selectedRows}
          getRowId={(row) => row.poItemId}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default AccountingInboundsTable;
